// Pin identity across order edits (#188). Pinned order cards are keyed by
// venue order id, but an edit may route through cancel + place — which mints
// a NEW id and would strand the pin on the retired order (it renders as a
// tombstone and the fresh order arrives unpinned). This module owns the two
// pieces the fix needs outside the big component: the key algebra (which pin
// key an order id maps to, and how a pin moves between keys) and the
// success/failure ordering (transfer only after the venue edit resolved,
// never on a same-id edit, never on a failed one).

/** A pinned watch-copy in the tracker's pinned strip. Keyed by stable card
 *  identity (position = ticker+side, order = venue order id); `label` is
 *  snapshotted at pin time so a tombstone can still name itself. A pinned
 *  KALSHI order is also skipped by Cancel All / Pull All (#943). Persisted per
 *  user by `stores/trackerPins`. */
export interface PinnedEntry {
  key: string;
  kind: 'position' | 'order';
  label: string;
  /** #1006: a Polymarket US pin — the account and view it was pinned from.
   *  US data is read one account and view at a time, so the strip uses this
   *  to say where the item lives when that selection is not on screen. */
  us?: UsPinOrigin;
}

export type UsPinOrigin = { accountId: string; view: 'own' | 'desk' };

/** #1006: Polymarket US pin keys. The `us-` prefix keeps them apart from
 *  Kalshi order ids and `poly-` keys; the account is part of the identity
 *  because the same market (or a copied intent id) on another account is a
 *  different item. Watch-only, like international Polymarket pins. */
export const US_PIN_PREFIX = 'us-';
export function usOrderPinKey(accountId: string, intentId: string): string {
  return `${US_PIN_PREFIX}o|${accountId}|${intentId}`;
}
export function usHoldingPinKey(accountId: string, marketSlug: string, outcome: 'yes' | 'no'): string {
  return `${US_PIN_PREFIX}p|${accountId}|${marketSlug}|${outcome}`;
}

export type OrderVenue = 'kalshi' | 'polymarket';

/** Pin key for an order, mirroring OrderCardVM.key: Kalshi pins use the raw
 *  venue order id; Polymarket pins are `poly-${id}` (the prefix keeps the two
 *  venues' ids from colliding in the shared key namespace). `editOrder`
 *  carries the raw cancel id for BOTH venues, so edit-path callers must map
 *  through this before touching pin state. */
export function orderPinKey(venue: OrderVenue, orderId: string): string {
  return venue === 'polymarket' ? `poly-${orderId}` : orderId;
}

/** True for a pinned Kalshi resting order — the only pins that protect an
 *  order from bulk cancels (#943). Polymarket order pins (`poly-` prefix),
 *  Polymarket US pins (`us-`, #1006) and position pins stay watch-only. */
export function isKalshiOrderPin(entry: PinnedEntry): boolean {
  return entry.kind === 'order' && !entry.key.startsWith('poly-') && !entry.key.startsWith(US_PIN_PREFIX);
}

/** Move an order pin from `previousKey` to `replacementKey` in place.
 *
 *  Only entries with `kind === 'order'` are eligible — a position pin whose
 *  key happens to collide with an order id is never touched. The transferred
 *  entry keeps its array slot and snapshotted label. If the replacement key
 *  is somehow already pinned, the stale previous-key entry is dropped and
 *  exactly one replacement-key entry survives. No-op cases (same key, or the
 *  previous key isn't pinned) return the input array unchanged so callers
 *  inside a functional setState don't churn state or re-persist. */
export function transferOrderPin(
  entries: PinnedEntry[],
  previousKey: string,
  replacementKey: string,
): PinnedEntry[] {
  if (previousKey === replacementKey) return entries;
  const isPreviousOrderPin = (e: PinnedEntry) => e.kind === 'order' && e.key === previousKey;
  if (!entries.some(isPreviousOrderPin)) return entries;
  const replacementAlreadyPinned = entries.some(
    (e) => e.kind === 'order' && e.key === replacementKey,
  );
  if (replacementAlreadyPinned) {
    return entries.filter((e) => !isPreviousOrderPin(e));
  }
  return entries.map((e) => (isPreviousOrderPin(e) ? { ...e, key: replacementKey } : e));
}

/** Run a venue order edit and transfer the pin iff it succeeded under a new
 *  id. Ordering contract (#188): the pin must never move before the venue
 *  confirmed the replacement — a failed edit leaves the old pin exactly where
 *  it was (tombstoning if the cancel leg already destroyed the order is the
 *  issue's required failure behavior). Same-id success (no-op / queue-
 *  preserving decrease) never calls `transfer`. */
export async function runOrderEditWithPinTransfer(
  previousOrderId: string,
  edit: () => Promise<{ orderId: string }>,
  transfer: (previousOrderId: string, replacementOrderId: string) => void,
): Promise<{ orderId: string }> {
  const result = await edit();
  if (result.orderId !== previousOrderId) {
    transfer(previousOrderId, result.orderId);
  }
  return result;
}
